import { asDate } from "@@/shared/date-time/as-date";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import type {
    TranslatableValue} from "@@/shared/translations/translation";
import {
    translation,
} from "@@/shared/translations/translation";
import { z } from "zod";

const weekdaysOnOffZodObject = z.object({
    monday: z.boolean(),
    tuesday: z.boolean(),
    wednesday: z.boolean(),
    thursday: z.boolean(),
    friday: z.boolean(),
    saturday: z.boolean(),
    sunday: z.boolean(),
});
type WeekdaysOnOff = z.infer<typeof weekdaysOnOffZodObject>;

const getWeekday = (date: IsoAndUnixTimestamp | Date) => {
    const _date = asDate(date);
    const weekday = (() => {
        if (_date.getDay() === 0) return "sunday";
        if (_date.getDay() === 1) return "monday";
        if (_date.getDay() === 2) return "tuesday";
        if (_date.getDay() === 3) return "wednesday";
        if (_date.getDay() === 4) return "thursday";
        if (_date.getDay() === 5) return "friday";
        if (_date.getDay() === 6) return "saturday";
        throw new Error("Invalid weekday; " + _date.getDay());
    })();
    return weekday;
};

const weekdaysOffDefault: WeekdaysOnOff = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
};
const weekdaysOnDefault: WeekdaysOnOff = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
};

const weekdays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
] as const;
type Weekday = (typeof weekdays)[number];
const isWeekdaysOnOffEqual = (a: WeekdaysOnOff, b: WeekdaysOnOff) => {
    return weekdays.every(weekday => a[weekday] === b[weekday]);
};
const weekdayMeta: {
    [key in Weekday]: {
        key: key;
        name: {
            short: TranslatableValue;
            singular: TranslatableValue;
            plural: TranslatableValue;
        };
    };
} = {
    monday: {
        key: "monday",
        name: {
            short: translation({
                sv: "mån",
                en: "Mon",
            }),
            singular: translation({
                sv: "måndag",
                en: "Monday",
            }),
            plural: translation({
                sv: "måndagar",
                en: "Mondays",
            }),
        },
    },
    tuesday: {
        key: "tuesday",
        name: {
            short: translation({
                sv: "tis",
                en: "Tue",
            }),
            singular: translation({
                sv: "tisdag",
                en: "Tuesday",
            }),
            plural: translation({
                sv: "tisdagar",
                en: "Tuesdays",
            }),
        },
    },
    wednesday: {
        key: "wednesday",
        name: {
            short: translation({
                sv: "ons",
                en: "Wed",
            }),
            singular: translation({
                sv: "onsdag",
                en: "Wednesday",
            }),
            plural: translation({
                sv: "onsdagar",
                en: "Wednesdays",
            }),
        },
    },
    thursday: {
        key: "thursday",
        name: {
            short: translation({
                sv: "tor",
                en: "Thu",
            }),
            singular: translation({
                sv: "torsdag",
                en: "Thursday",
            }),
            plural: translation({
                sv: "torsdagar",
                en: "Thursdays",
            }),
        },
    },
    friday: {
        key: "friday",
        name: {
            short: translation({
                sv: "fre",
                en: "Fri",
            }),
            singular: translation({
                sv: "fredag",
                en: "Friday",
            }),
            plural: translation({
                sv: "fredagar",
                en: "Fridays",
            }),
        },
    },
    saturday: {
        key: "saturday",
        name: {
            short: translation({
                sv: "lör",
                en: "Sat",
            }),
            singular: translation({
                sv: "lördag",
                en: "Saturday",
            }),
            plural: translation({
                sv: "lördagar",
                en: "Saturdays",
            }),
        },
    },
    sunday: {
        key: "sunday",
        name: {
            short: translation({
                sv: "sön",
                en: "Sun",
            }),
            singular: translation({
                sv: "söndag",
                en: "Sunday",
            }),
            plural: translation({
                sv: "söndagar",
                en: "Sundays",
            }),
        },
    },
};
const weekdaysMeta = weekdays.map(weekday => weekdayMeta[weekday]);

const isWeekDaysAllOn = (weekdaysOnOff: WeekdaysOnOff) => {
    return weekdays.every(weekday => weekdaysOnOff[weekday]);
};

const isWeekDaysAllOff = (weekdaysOnOff: WeekdaysOnOff) => {
    return weekdays.every(weekday => !weekdaysOnOff[weekday]);
};

export {
    getWeekday,
    isWeekDaysAllOff,
    isWeekDaysAllOn,
    isWeekdaysOnOffEqual,
    weekdayMeta,
    weekdays,
    weekdaysMeta,
    weekdaysOffDefault,
    weekdaysOnDefault,
    weekdaysOnOffZodObject,
};
export type { Weekday, WeekdaysOnOff };
